/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { Button, Module, Node } from '@msdyn365-commerce-modules/utilities';
import * as Msdyn365 from '@msdyn365-commerce/core';
import { toJS } from 'mobx';
import * as React from 'react';

import {
    IAccountPreference,
    IAccountPreferences,
    IAccountProfileData,
    IAccountProfileItem,
    IAccountProfileViewProps
} from '@msdyn365-commerce-modules/account-management';
import { toast, Toaster } from 'sonner';
import {
    DateLocale,
    getDateFormat,
    getNumberFormatByLocale,
    INumberFormat,
    mapNumberFormatToLocale,
    NumberLocale,
    PreferredUnit,
    Separator,
    separatorFormats
} from '../../../actions/user-preferences/get-localization.action';
import postLocalizationAction, {
    IPostLocalizationData,
    PostLocalizationInput
} from '../../../actions/user-preferences/post-localization.action';
import { ILocalizationState, LocalizationStateInput, localizationStateSetItemAction } from '../../../states/localization-state';
import { runActionAndHandleAuth } from '../../../utilities/is-authenticated';
import {
    IAccountProfileConfig,
    IAccountProfileResources as IAccountProfileResourcesExt,
    IDisplayPreferencesHeadingData
} from '../definition-extensions/account-profile.ext.props.autogenerated';
import { mapPreferredUnitToText } from '../../../utilities/map-preferred-unit-to-text';

export interface IAccountProfileDataExtended extends IAccountProfileData {
    localizationState: ILocalizationState;
    resources: IAccountProfileResourcesExt;
}

const AccountPreferencesSection: React.FC<IAccountPreferences> = ({ accountPreferences, heading, personalization, extraPreferences }) => {
    return (
        <Node {...accountPreferences}>
            {heading}
            <AccountPreferenceSection {...personalization} />
            {extraPreferences &&
                extraPreferences.map((preferenceSecton, index) => {
                    return <AccountPreferenceSection {...preferenceSecton} key={index} />;
                })}
        </Node>
    );
};

const AccountPreferenceSection: React.FC<IAccountPreference> = ({
    accountPreference,
    heading,
    description,
    buttonWrapper,
    buttonYesText,
    button,
    buttonNoText
}) => {
    return (
        <Node {...accountPreference}>
            {heading}
            {description}
            <Node {...buttonWrapper}>
                {buttonNoText}
                {button}
                {buttonYesText}
            </Node>
        </Node>
    );
};

const AccountProfileSection: React.FC<IAccountProfileItem> = ({ AccountProfileItem, heading, links, description }) => {
    return (
        <Node {...AccountProfileItem}>
            {heading}
            {description}
            {links}
        </Node>
    );
};

const AccountDisplaySettingsSection: React.FC<{
    heading: IDisplayPreferencesHeadingData;
    CLASS_NAME: string;
    resources: IAccountProfileResourcesExt | any;
    localizationState?: ILocalizationState;
    context: Msdyn365.ICoreContext;
}> = ({ heading, CLASS_NAME, resources, localizationState, context }) => {
    const [selectedDateLocale, setSelectedDateLocale] = React.useState<DateLocale>(localizationState?.dateLocale as DateLocale);
    const [selectedNumberFormat, setSelectedNumberFormat] = React.useState<INumberFormat>(
        (localizationState?.numberLocale as unknown) as INumberFormat
    );
    const [selectedUnit, setSelectedUnit] = React.useState<string>(localizationState?.preferredUnit || '');
    const [isSaveDisabled, setIsSaveDisabled] = React.useState<boolean>(true);

    const handleDateChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        checkIfSaveShouldBeEnabled(event.target.value as DateLocale, selectedNumberFormat);
        setSelectedDateLocale(event.target.value as DateLocale);
    };

    const handleNumberFormatChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const selectedSeparator = event.target.value as Separator;
        const selectedFormat = separatorFormats[selectedSeparator];
        checkIfSaveShouldBeEnabled(selectedDateLocale, selectedFormat);
        setSelectedNumberFormat(selectedFormat);
    };

    const handleUnitChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const newUnit = event.target.value;
        setSelectedUnit(newUnit);
        checkIfSaveShouldBeEnabled(selectedDateLocale, selectedNumberFormat);
    };

    const checkIfSaveShouldBeEnabled = (dateLocale: DateLocale, numberFormat: INumberFormat) => {
        if (dateLocale && numberFormat) {
            setIsSaveDisabled(false);
        } else {
            setIsSaveDisabled(true);
        }
    };

    const removeLoadingCursor = () => {
        if (typeof window !== 'undefined' && window.document) {
            window.document.body.style.cursor = 'default';
        }
    };

    const saveDisplayPreferences = async () => {
        if (typeof window !== 'undefined' && window.document) {
            window.document.body.style.cursor = 'wait';
        }

        const selectedNumberLocale = mapNumberFormatToLocale(selectedNumberFormat);
        const userInfoInput: IPostLocalizationData = {
            user: {
                NumberFormat: selectedNumberLocale,
                DateFormat: selectedDateLocale,
                PreferredUnit: selectedUnit
            }
        };

        localizationStateSetItemAction(new LocalizationStateInput(), {
            dateLocale: selectedDateLocale,
            numberLocale: selectedNumberFormat,
            preferredUnit: selectedUnit
        });

        const showSavePreferencesSuccessToast = () => {
            removeLoadingCursor();

            toast.success(
                <div className='toast' role='alert'>
                    <div className='toast-header'>
                        <h4>{resources.accountProfileDisplaySettingsToastSaved}</h4>
                        <button className='toast-close' onClick={() => toast.dismiss()}></button>
                    </div>
                </div>
            );
        };

        const actionInput = new PostLocalizationInput(userInfoInput);
        await runActionAndHandleAuth(
            () =>
                postLocalizationAction(actionInput, context.actionContext).then(() => {
                    showSavePreferencesSuccessToast();
                }),
            context.request.user
        );
    };

    return (
        <>
            <Toaster
                richColors
                position='top-right'
                toastOptions={{
                    classNames: {
                        default: 'toast-light',
                        error: 'toast-danger',
                        success: 'toast-success',
                        warning: 'toast-warning',
                        info: 'toast-info'
                    }
                }}
            />
            <Msdyn365.Text
                className={`${CLASS_NAME}-tile__heading`}
                tag={heading?.tag || 'h2'}
                text={heading?.text || 'Display preferences'}
            />
            <div className={`${CLASS_NAME}__section-display-settings-wrapper`}>
                <div>
                    <label className={`${CLASS_NAME}__display-date-title`} htmlFor={`${CLASS_NAME}__display-date-select`}>
                        {resources.accountProfileDisplaySettingsDateFormatTitle}
                    </label>
                    <select
                        id={`${CLASS_NAME}__display-date-select`}
                        className={`msc-form-control msc-dropdown__select`}
                        value={selectedDateLocale}
                        onChange={handleDateChange}
                    >
                        <option value=''>{resources.accountProfileDisplaySettingsPleaseSelectDate}</option>
                        {Object.entries(DateLocale).map(([_, value]) => (
                            <option value={value}>{getDateFormat(value)}</option>
                        ))}
                    </select>
                </div>
                <div>
                    <label className={`${CLASS_NAME}__display-number-title`} htmlFor={`${CLASS_NAME}__display-number-select`}>
                        {resources.accountProfileDisplaySettingsNumberFormatTitle}
                    </label>

                    <select
                        id={`${CLASS_NAME}__display-number-select`}
                        className={`msc-form-control msc-dropdown__select`}
                        value={selectedNumberFormat?.decimalSeparator}
                        onChange={handleNumberFormatChange}
                    >
                        <option value=''>{resources.accountProfileDisplaySettingsPleaseSelectNumber}</option>
                        {Object.entries(NumberLocale).map(([key, value]) => {
                            const numberFormat = getNumberFormatByLocale(key);
                            return <option value={numberFormat?.decimalSeparator as string}>{value}</option>;
                        })}
                    </select>
                </div>
                <div>
                    <label className={`${CLASS_NAME}__display-unit-title`} htmlFor={`${CLASS_NAME}__display-unit-select`}>
                        {resources.accountProfileDisplaySettingsPreferredUnitTitle}
                    </label>

                    <select
                        id={`${CLASS_NAME}__display-unit-select`}
                        className='msc-form-control msc-dropdown__select'
                        value={selectedUnit}
                        onChange={handleUnitChange}
                    >
                        <option value=''>{resources.accountProfileDisplaySettingsPleaseSelectUnit}</option>
                        {Object.entries(PreferredUnit).map(([_, value]) => (
                            <option value={value}>{mapPreferredUnitToText(value, resources)}</option>
                        ))}
                    </select>
                </div>
            </div>
            <Button
                className={`${CLASS_NAME}__section-display-settings-save-btn`}
                disabled={isSaveDisabled}
                onClick={() => {
                    saveDisplayPreferences();
                }}
                title={resources.accountProfileDisplaySettingsSaveBtnTitle}
                aria-label={resources.accountProfileDisplaySettingsSaveBtnTitle}
            >
                {resources.accountProfileDisplaySettingsSaveBtnTitle}
            </Button>
        </>
    );
};

const AccountProfileSettingsSection: React.FC<{
    heading: React.ReactNode;
    emailSection: IAccountProfileItem;
    nameSection: IAccountProfileItem;
}> = ({ heading, emailSection, nameSection }) => {
    return (
        <>
            {heading}
            <AccountProfileSection {...emailSection} />
            <AccountProfileSection {...nameSection} />
        </>
    );
};

const AccountProfileView: React.FC<IAccountProfileViewProps> = props => {
    const {
        AccountProfile,
        infoMessageBar,
        accountProfileWrapper,
        heading,
        emailSection,
        nameSection,
        preferenceSection,
        customerAttributesWrapper,
        customerAttributesSection,
        config,
        resources,
        context
    } = props;
    const showPreferences = !(config as IAccountProfileConfig)?.hidePreferences;
    const displayPreferencesHeading = (config as IAccountProfileConfig)?.displayPreferencesHeading;
    const CLASS_NAME = 'ms-account-profile';
    const profileData = props.data as IAccountProfileDataExtended;

    const localizationState = toJS(profileData.localizationState)?.result as ILocalizationState;

    return (
        <Module {...AccountProfile}>
            {infoMessageBar}
            {accountProfileWrapper && (
                <Node {...accountProfileWrapper}>
                    <Node className={`${CLASS_NAME}__section-personal-settings`}>
                        <AccountProfileSettingsSection heading={heading} emailSection={emailSection} nameSection={nameSection} />
                    </Node>
                    <Node className={`${CLASS_NAME}__section-display-settings`}>
                        {localizationState && (
                            <AccountDisplaySettingsSection
                                heading={displayPreferencesHeading}
                                CLASS_NAME={CLASS_NAME}
                                resources={resources}
                                localizationState={localizationState}
                                context={context}
                            />
                        )}
                    </Node>
                    {showPreferences && <AccountPreferencesSection {...preferenceSection} />}
                </Node>
            )}
            {customerAttributesWrapper && <Node {...customerAttributesWrapper}>{customerAttributesSection}</Node>}
        </Module>
    );
};

export default AccountProfileView;
